import React, { useState, useEffect } from 'react';
import likesService from '../services/likesService';
import LikeNotification from './LikeNotification';

const LikeButton = ({ 
  entityId, 
  entityType, 
  userId, 
  initialLikeCount = 0,
  showCount = true,
  size = 'medium',
  className = ''
}) => {
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (userId && entityId) {
      fetchLikeStats();
    }
  }, [userId, entityId, entityType]);

  const fetchLikeStats = async () => {
    try {
      const response = await likesService.getLikeStats(entityId, entityType);
      setLikeCount(response.totalLikes);
    } catch (error) {
      console.error('Error fetching like stats:', error);
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
  };

  const handleLikeToggle = async () => {
    if (!userId) {
      showNotification('Please log in to like this item', 'warning');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Always like (no unlike functionality)
      const response = await likesService.createLike(userId, entityId, entityType);
      setLikeCount(prev => prev + 1);
      
      // Show success message with remaining likes if available
      if (response.remainingLikes !== undefined) {
        if (response.remainingLikes > 0) {
          showNotification(`Liked! You can like ${response.remainingLikes} more items this period`, 'success');
        } else {
          showNotification('Liked! You\'ve reached your like limit for this period', 'success');
        }
      } else {
        showNotification('Liked successfully!', 'success');
      }
    } catch (error) {
      let errorMessage = 'Something went wrong. Please try again.';
      
      // Handle specific error cases
      if (error.message) {
        if (error.message.includes('limit exceeded')) {
          errorMessage = 'You\'ve reached your like limit for this period. Please upgrade your subscription for more likes.';
        } else if (error.message.includes('not found')) {
          errorMessage = 'This item is no longer available';
        } else if (error.message.includes('authentication')) {
          errorMessage = 'Please log in again to continue';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Like error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-8 h-8 text-sm';
      case 'large':
        return 'w-12 h-12 text-lg';
      default:
        return 'w-10 h-10 text-base';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 'w-4 h-4';
      case 'large':
        return 'w-6 h-6';
      default:
        return 'w-5 h-5';
    }
  };

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={handleLikeToggle}
          disabled={isLoading}
          className={`
            ${getSizeClasses()}
            flex items-center justify-center
            rounded-full transition-all duration-200
            bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white
            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900
          `}
          title="Like"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg 
              className={getIconSize()} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
              />
            </svg>
          )}
        </button>
        
        {showCount && (
          <span className="text-gray-300 font-medium">
            {likeCount.toLocaleString()}
          </span>
        )}
      </div>
      
      {notification && (
        <LikeNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
};

export default LikeButton; 